import { useMemo } from "react";
import { AvailableActor } from "src/contexts/documentationArea/domain/AvailableActor";
import { ActorId } from "src/contexts/documentationArea/domain/ActorId";

interface UseActorSelectorParams {
  availableActors: AvailableActor[];
  selectedActorId: ActorId | null;
}

interface UseActorSelectorResult {
  ownerActor: AvailableActor | null;
  readOnlyActors: AvailableActor[];
  selectedActor: AvailableActor | null;
  isOwner: (actorId: ActorId) => boolean;
  hasMultipleActors: boolean;
}

export function useActorSelector({
  availableActors,
  selectedActorId,
}: UseActorSelectorParams): UseActorSelectorResult {
  const editableActors = availableActors.filter((a) => !a.readOnly);

  // Strict sectioning rule: readOnly false => own section, otherwise others.
  const ownerActor =
    (selectedActorId ? editableActors.find((a) => a.id === selectedActorId) : undefined) ??
    editableActors[0] ??
    null;

  const readOnlyActors = availableActors.filter((a) => a.readOnly);
  const hasMultipleActors = availableActors.length > 1;

  const selectedActor = useMemo(() => {
    // Try to find by selected ID (owner first, then read-only actors)
    if (ownerActor && ownerActor.id === selectedActorId) {
      return ownerActor;
    }

    const bySelectedId = readOnlyActors.find((actor) => actor.id === selectedActorId);
    if (bySelectedId) return bySelectedId;

    // Final fallback: owner > first read-only > null
    return ownerActor ?? readOnlyActors[0] ?? null;
  }, [ownerActor, readOnlyActors, selectedActorId]);

  // Helper to check if an actor is the owner (readOnly: false)
  const isOwner = (actorId: ActorId) => ownerActor?.id === actorId;

  return {
    ownerActor,
    readOnlyActors,
    selectedActor,
    isOwner,
    hasMultipleActors,
  };
}
